import { useEffect } from 'react';

import { useCurrentMeetingDataContext } from '@/features/meeting/context/CurrentMeetingDataContext';
import { usePrevious } from '@/utils/helpers/react.helper';

type Args<TData> = {
  value: TData | null;
  callback: (oldValue: TData | null, newValue: TData | null) => void;
  options?: {
    customSkipIfTrue?: () => boolean;
  };
};

export const useOnValueChange = <TData>({ value, callback, options }: Args<TData>) => {
  const lastValue = usePrevious(value);

  useEffect(() => {
    if (options?.customSkipIfTrue?.()) {
      return;
    }

    if (lastValue !== value) {
      callback(lastValue ?? null, value);
    }
  }, [value]);
};

type OnCaseChangeArgs = {
  callback: (oldValue: string | null, newValue: string | null) => void;
  options?: {
    skipIfPreview?: boolean;
    skipIfTesting?: boolean;
    skipIfNewValueIsNull?: boolean;
    customSkipIfTrue?: () => boolean;
  };
};

export const useOnCaseChange = ({ callback, options }: OnCaseChangeArgs) => {
  const {
    currentPatientCaseId: currentCaseId,
    isPreview,
    currentMeeting: meeting,
  } = useCurrentMeetingDataContext();

  useOnValueChange({
    value: currentCaseId,
    callback,
    options: {
      customSkipIfTrue: () => {
        if (
          (options?.skipIfPreview && isPreview) ||
          (options?.skipIfTesting && meeting?.isInTesting) ||
          (options?.skipIfNewValueIsNull && !currentCaseId) ||
          options?.customSkipIfTrue?.()
        )
          return true;
        return false;
      },
    },
  });
};

export const useOnSlideChange = ({ callback, options }: OnCaseChangeArgs) => {
  const { slideID, isPreview, currentMeeting: meeting } = useCurrentMeetingDataContext();

  useOnValueChange({
    value: slideID,
    callback,
    options: {
      customSkipIfTrue: () => {
        if (
          (options?.skipIfPreview && isPreview) ||
          (options?.skipIfTesting && meeting?.isInTesting) ||
          (options?.skipIfNewValueIsNull && !slideID) ||
          options?.customSkipIfTrue?.()
        ) {
          return true;
        }
        return false;
      },
    },
  });
};
